/**
 * Done. / ChiroCPD — TopNav Feature Component
 *
 * Desktop: horizontal links. Mobile: hamburger menu.
 * Active state = underline + semibold (NOT colour alone — WCAG 1.4.1).
 * Expired subscription: links are aria-disabled="true" + aria-describedby.
 * Hamburger: aria-expanded, aria-controls, Escape closes, focus returns to trigger,
 *            inert on <main> while open.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useRoute } from 'wouter';
import { cx, getInitials, isSubscriptionActive } from '@/lib/utils';
import type { SubscriptionStatus } from '@/lib/types';

interface NavLink {
  href: string;
  label: string;
}

const NAV_LINKS: NavLink[] = [
  { href: '/dashboard',  label: 'Dashboard'   },
  { href: '/activities', label: 'Activities'  },
  { href: '/export',     label: 'Export'      },
];

interface TopNavProps {
  subscriptionStatus: SubscriptionStatus;
  user: { name: string; initials?: string };
  currentRoute: string;
}

// ── Route-active helper (wouter) ──────────────────────────────────────────────
function NavLink({
  href,
  label,
  isActive,
  isDisabled,
  onNavigate,
}: {
  href: string;
  label: string;
  isActive: boolean;
  isDisabled: boolean;
  onNavigate?: (() => void) | undefined;
}): React.ReactElement {
  if (isDisabled) {
    return (
      <span
        aria-disabled="true"
        aria-describedby="lockout-reason"
        className="px-1 py-0.5 text-sm font-medium text-[var(--text-muted)] cursor-not-allowed select-none"
      >
        {label}
      </span>
    );
  }

  return (
    <Link
      href={href}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate ? () => { onNavigate(); } : undefined}
      className={cx(
        'px-1 py-0.5 text-sm transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)] rounded',
        isActive
          ? 'font-semibold text-[var(--color-done-700)] border-b-2 border-[var(--color-done-600)]'
          : 'font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
      )}
    >
      {label}
    </Link>
  );
}

export function TopNav({ subscriptionStatus, user }: TopNavProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const active = isSubscriptionActive(subscriptionStatus);
  const initials = user.initials ?? getInitials(user.name);

  // Close on Escape, return focus to trigger
  const handleKeyDown = useCallback((e: KeyboardEvent): void => {
    if (e.key === 'Escape' && menuOpen) {
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  }, [menuOpen]);



  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [handleKeyDown]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, [menuOpen]);

  // Route checks — wouter hooks must be called unconditionally
  const [isDashboard] = useRoute('/dashboard');
  const [isActivities] = useRoute('/activities');
  const [isExport] = useRoute('/export');

  const routeMap: Record<string, boolean> = {
    '/dashboard':  isDashboard,
    '/activities': isActivities,
    '/export':     isExport,
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[var(--surface-base)] border-b border-[var(--border-base)] shadow-sm"
      data-print-hidden
    >
      {/* Hidden lockout reason for aria-describedby on disabled links */}
      {!active && (
        <span id="lockout-reason" className="sr-only">
          Navigation disabled — subscription inactive. Resubscribe to restore access.
        </span>
      )}

      <div className="mx-auto max-w-5xl px-4 h-14 flex items-center justify-between">
        {/* Brand */}
        <Link
          href="/dashboard"
          className="flex items-center gap-2 text-[var(--color-done-700)] font-semibold text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)] rounded"
        >
          <span className="text-lg font-bold tracking-tight">ChiroCPD</span>
        </Link>

        {/* Desktop nav */}
        <nav
          aria-label="Main navigation"
          className="hidden sm:flex items-center gap-6"
        >
          {NAV_LINKS.map(link => (
            <NavLink
              key={link.href}
              href={link.href}
              label={link.label}
              isActive={routeMap[link.href] ?? false}
              isDisabled={!active}
            />
          ))}
        </nav>

        {/* User avatar + mobile hamburger */}
        <div className="flex items-center gap-3">
          {/* User initials avatar */}
          <div
            aria-label={`User: ${user.name}`}
            className="h-8 w-8 rounded-full bg-[var(--color-done-100)] text-[var(--color-done-700)] text-sm font-semibold flex items-center justify-center select-none"
          >
            {initials}
          </div>

          {/* Mobile hamburger */}
          <button
            ref={menuButtonRef}
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={() => { setMenuOpen(o => !o); }}
            className="sm:hidden p-2 rounded-[var(--radius-md)] text-[var(--text-secondary)] hover:bg-[var(--surface-muted)] min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)]"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          id="mobile-menu"
          ref={menuRef}
          className="sm:hidden border-t border-[var(--border-base)] bg-[var(--surface-base)] px-4 py-3"
        >
          <nav aria-label="Mobile navigation" className="flex flex-col gap-1">
            {NAV_LINKS.map(link => (
              <div key={link.href} className="py-1">
                <NavLink
                  href={link.href}
                  label={link.label}
                  isActive={routeMap[link.href] ?? false}
                  isDisabled={!active}
                  onNavigate={() => { setMenuOpen(false); }}
                />
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
